import React, { useEffect, useMemo, useState } from "react";
import PageTitle from "../../../components/common/PageTitle";
import { injectIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import {
  getPermissionsRequest,
  getRolesRequest,
  syncUserPermissionsRequest,
} from "../../../redux/actions/Company";
import { json, useNavigate, useParams } from "react-router-dom";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const Team = ({ intl }) => {
  const dispatch = useDispatch();
  const { token } = useSelector(({ auth }) => auth);
  const { id } = useParams();
  const { permissions, roles } = useSelector(({ company }) => company);
  const { locale } = useSelector(({ common }) => common);
  const navigate = useNavigate();

  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [selectedModule, setSelectedModule] = useState("");
  const [selectedRole, setSelectedRole] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [previewPermissions, setPreviewPermissions] = useState([]);
  const [originalPermissions, setOriginalPermissions] = useState([]);

  useEffect(() => {
    if (permissions?.user_permissions) {
      const userPermissionIds = permissions.user_permissions.map((p) => p.id);
      
      setSelectedPermissions(userPermissionIds);
      setOriginalPermissions(userPermissionIds);
    }
  }, [permissions]);

  useEffect(() => {
    if (id) {
      dispatch(getPermissionsRequest(token, id, intl));
    }
  }, [id]);

  useEffect(() => {
    dispatch(getRolesRequest(token));
  }, [dispatch, token]);

  useEffect(() => {
    if (permissions?.user_permissions) {
      const userPermissionIds = permissions.user_permissions.map((p) => p.id);
      setSelectedPermissions(userPermissionIds);
    }
  }, [permissions]);

  const handleCheckboxChange = (permissionId) => {
    setSelectedPermissions((prev) =>
      prev.includes(permissionId)
        ? prev.filter((id) => id !== permissionId)
        : [...prev, permissionId]
    );
  };

  const groupedPermissions = useMemo(() => {
    return permissions?.app_permissions?.reduce((acc, permission) => {
      const module = permission.module || "Other";
      if (!acc[module]) acc[module] = [];
      acc[module].push(permission);
      return acc;
    }, {});
  }, [permissions]);

  const modulesToDisplay = useMemo(() => {
    if (!groupedPermissions) return {};

    if (selectedModule) {
      return groupedPermissions[selectedModule]
        ? { [selectedModule]: groupedPermissions[selectedModule] }
        : {};
    }
    return groupedPermissions;
  }, [selectedModule, groupedPermissions]);

  const handleSyncPermission = () => {
    dispatch(syncUserPermissionsRequest(token, id, selectedPermissions, intl));
    setShowModal(false);
  };

  const handleRoleChange = (event) => {
    const value = event.target.value;
    setSelectedRole(value);
  };

  const handleApplyRole = () => {
    const role = roles.roles.find((r) => r.id === selectedRole);
    if (role) {
      const rolePermissionIds = role.permissions.map((p) => p.id);
      
      setPreviewPermissions(rolePermissionIds);
      setSelectedPermissions(rolePermissionIds);
      setShowModal(true);
    }
  };

  const fullname =
    permissions?.contact?.first_name + " " + permissions?.contact?.last_name;

  const selectedRoleName = roles?.roles?.find(
    (role) => role.id === selectedRole
  )?.meta.translations[locale.short];

  return (
    <div className="min-h-screen_custom w-full flex flex-col items-center px-4 py-8 gap-3">
      <PageTitle title="Permission Management" name={fullname} />
      <div className="w-full max-w-6xl md:gap-3">
        <div className="w-full flex flex-col">
          <div className="w-full flex justify-end">
            <div className="w-1/5">
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel id="module-select-label">
                  {intl.formatMessage({ id: "filterByModule" })}
                </InputLabel>
                <Select
                  labelId="module-select-label"
                  id="module-select"
                  value={selectedModule}
                  onChange={(e) => setSelectedModule(e.target.value)}
                  label={intl.formatMessage({ id: "filterByModule" })}
                >
                  <MenuItem value="">
                    {intl.formatMessage({ id: "All Modules" })}
                  </MenuItem>
                  {Object.keys(groupedPermissions || {}).map((mod) => {
                    const meta = (() => {
                      try {
                        const firstPerm = groupedPermissions[mod]?.[0];
                        return (
                          JSON.parse(firstPerm?.meta)?.module?.translations[
                            locale.short
                          ] || mod
                        );
                      } catch {
                        return mod;
                      }
                    })();
                    return (
                      <MenuItem key={mod} value={mod}>
                        {meta}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="w-full md:w-1/5 flex items-center gap-2">
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel id="role-select-label">
                  {intl.formatMessage({ id: "Apply a role" })}
                </InputLabel>
                <Select
                  labelId="role-select-label"
                  id="role-select"
                  value={selectedRole}
                  onChange={handleRoleChange}
                  label={intl.formatMessage({ id: "Apply a role" })}
                >
                  <MenuItem value="">
                    {intl.formatMessage({ id: "None" })}
                  </MenuItem>
                  {roles?.roles?.map((role) => (
                    <MenuItem key={role.id} value={role.id}>
                      {role.meta.translations[locale.short]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <button
              onClick={handleApplyRole}
              className="bg-secondary text-white px-4 py-2 rounded-full"
              disabled={!selectedRole}
            >
              {intl.formatMessage({ id: "Apply" })}
            </button>
          </div>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center">
          <div className="bg-white w-full max-w-6xl max-h-[90vh] overflow-auto p-6 rounded-2xl shadow-xl relative">
            <PageTitle
              title="Permissions related to the role"
              name={selectedRoleName}
            />

            <span className="flex justify-center items-center w-full mb-3 text-gray-500">
              {intl.formatMessage({ id: "permissions_applied_notice" })}
            </span>

            <button
              onClick={() => {
                setSelectedPermissions(originalPermissions);
                setShowModal(false);
              }}
              className="absolute top-2 right-4 hover:text-black"
            >
              <i className="fa fa-x" aria-hidden="true"></i>
            </button>

            <div className="space-y-6">
              {(() => {
                const groupedModulesByOrder = {};

                Object.entries(groupedPermissions || {}).forEach(
                  ([moduleName, perms]) => {
                    try {
                      const meta = JSON.parse(perms[0]?.meta);
                      const moduleOrder = meta?.module?.order;

                      if (!groupedModulesByOrder[moduleOrder]) {
                        groupedModulesByOrder[moduleOrder] = [];
                      }

                      groupedModulesByOrder[moduleOrder].push({
                        moduleName,
                        perms,
                      });
                    } catch (e) {
                      console.error("Erreur lors du parsing du module", e);
                    }
                  }
                );

                const sortedModuleOrders = Object.keys(groupedModulesByOrder)
                  ?.map(Number)
                  .sort((a, b) => a - b);

                return sortedModuleOrders?.map((order) =>
                  groupedModulesByOrder[order]?.map(({ moduleName, perms }) => {
                    const moduleLabel = (() => {
                      try {
                        const meta = JSON.parse(perms[0]?.meta);
                        return meta?.module?.translations[locale.short];
                      } catch {
                        return moduleName;
                      }
                    })();
                    
                    const sortedPerms = [...perms].sort((a, b) => {
                      try {
                        const orderA =
                          JSON.parse(a.meta)?.permission?.order ?? 0;
                        const orderB =
                          JSON.parse(b.meta)?.permission?.order ?? 0;
                        return orderA - orderB;
                      } catch {
                        return 0;
                      }
                    });

                    return (
                      <div
                        key={moduleName}
                        className="shadow-xl border border-gray-300 rounded-3xl pl-8 pr-6 py-4 bg-[#f9f9f9]"
                      >
                        <div className="mb-2 font-semibold text-lg">
                          {moduleLabel}
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 pl-4">
                          {sortedPerms.map((perm) => {
                            const label = (() => {
                              try {
                                const meta = JSON.parse(perm.meta);
                                return meta?.permission?.translations[
                                  locale.short
                                ];
                              } catch {
                                return perm.name;
                              }
                            })();

                            

                            const hasPermission = selectedPermissions.includes(
                              perm.id
                            );

                            return (
                              <div
                                key={perm.id}
                                className="flex items-center gap-2 text-base text-gray-700"
                              >
                                <i
                                  className={`fa-solid ${
                                    hasPermission
                                      ? "fa-circle-check text-green-600"
                                      : "fa-circle-xmark text-red-500"
                                  }`}
                                ></i>
                                <span>{label}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })
                );
              })()}
            </div>

            <div className="flex justify-center items-center gap-4 mt-6">
              <button
                type="button"
                className="!px-6 !py-2 !rounded-full border-2 border-secondary text-secondary"
                onClick={() => {
                  setSelectedPermissions(originalPermissions);
                  setShowModal(false);
                }}
              >
                {intl.formatMessage({ id: "cancel" })}
              </button>
              <button
                type="button"
                className="px-8 py-2 bg-secondary text-white rounded-full"
                onClick={handleSyncPermission}
              >
                {intl.formatMessage({ id: "confirm" })}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="w-full max-w-6xl space-y-4">
        {(() => {
          const groupedModulesByOrder = {};

          Object.entries(modulesToDisplay || {}).forEach(
            ([moduleName, perms]) => {
              try {
                const meta = JSON.parse(perms[0]?.meta);
                const moduleOrder = meta?.module?.order;


                if (!groupedModulesByOrder[moduleOrder]) {
                  groupedModulesByOrder[moduleOrder] = [];
                }

                groupedModulesByOrder[moduleOrder].push({ moduleName, perms });
              } catch (error) {
                console.error("Erreur parsing meta", error);
              }
            }
          );

          const sortedModuleOrders = Object.keys(groupedModulesByOrder)
            ?.map(Number)
            .sort((a, b) => a - b);
          

          return sortedModuleOrders?.map((moduleOrder) =>
            groupedModulesByOrder[moduleOrder]?.map(({ moduleName, perms }) => {
              const allChecked = perms.every((p) =>
                selectedPermissions.includes(p.id)
              );
              const someChecked = perms.some((p) =>
                selectedPermissions.includes(p.id)
              );

              const moduleEnglishName = (() => {
                try {
                  const meta = JSON.parse(perms[0]?.meta);
                  return meta?.module?.translations?.en || "";
                } catch {
                  return "";
                }
              })();

              const isReadOnly =
                    moduleEnglishName === "Profile" || moduleEnglishName === "Referral";

              const handleParentChange = (event) => {
                if (isReadOnly) return;
                const permissionIds = perms.map((p) => p.id);
                setSelectedPermissions((prev) =>
                  event.target.checked
                    ? [...new Set([...prev, ...permissionIds])]
                    : prev.filter((id) => !permissionIds.includes(id))
                );
              };

              const handleChildChange = (permissionId) => {
                if (isReadOnly) return;
                setSelectedPermissions((prev) =>
                  prev.includes(permissionId)
                    ? prev.filter((id) => id !== permissionId)
                    : [...prev, permissionId]
                );
              };

              const moduleLabel = (() => {
                try {
                  const meta = JSON.parse(perms[0]?.meta);
                  return meta?.module?.translations[locale.short];
                } catch {
                  return moduleName;
                }
              })();

              const sortedPerms = [...perms].sort((a, b) => {
                try {
                  const aOrder = JSON.parse(a.meta)?.permission?.order ?? 0;
                  const bOrder = JSON.parse(b.meta)?.permission?.order ?? 0;
                  return aOrder - bOrder;
                } catch {
                  return 0;
                }
              });

              return (
                <Accordion
                  key={moduleName}
                  className="shadow-xl border border-gray-300 rounded-3xl pl-8 overflow-hidden"
                  disableGutters
                  sx={{
                    mb: 2,
                    borderRadius: "2rem !important",
                    backgroundColor: "#f9f9f9",
                    boxShadow: "0 10px 20px rgba(0, 0, 0, 0.08)",
                    overflow: "hidden",
                    border: "1px solid #e0e0e0",
                    px: 2,
                  }}
                  square={false}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={allChecked}
                          indeterminate={!allChecked && someChecked}
                          onChange={handleParentChange}
                          onClick={(e) => e.stopPropagation()}
                          style={isReadOnly ? { pointerEvents: "none", color: 'gray' } : {}}
                        />
                      }
                      label={
                        <Typography className="font-semibold">
                          {moduleLabel}
                        </Typography>
                      }
                      onClick={(e) => e.stopPropagation()}
                    />
                  </AccordionSummary>

                  <AccordionDetails>
                    <Grid container spacing={0} pl={4}>
                      {sortedPerms.map((perm) => {
                        const label = (() => {
                          try {
                            const meta = JSON.parse(perm?.meta);
                            return meta?.permission?.translations[locale.short];
                          } catch {
                            return perm?.name;
                          }
                        })();

                        return (
                          <Grid item xs={12} sm={6} md={4} key={perm.id}>
                            <FormControlLabel
                              label={label}
                              control={
                                <Checkbox
                                  checked={selectedPermissions.includes(
                                    perm.id
                                  )}
                                  onChange={() => handleChildChange(perm.id)}
                                  style={
                                    isReadOnly ? { pointerEvents: "none", color: 'gray' } : {}
                                  }
                                />
                              }
                            />
                          </Grid>
                        );
                      })}
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              );
            })
          );
        })()}
      </div>

      <div className="flex justify-center items-center gap-4">
        <button
          type="button"
          className="!px-6 !py-2 !rounded-full border-2 border-secondary text-secondary"
          onClick={() => navigate("/company/collab")}
        >
          {intl.formatMessage({ id: "back" })}
        </button>
        <button
          type="button"
          className="px-8 py-2 bg-secondary text-white rounded-full"
          onClick={handleSyncPermission}
        >
          {intl.formatMessage({ id: "save" })}
        </button>
      </div>
    </div>
  );
};

export default injectIntl(Team);
